import {
  ApiTransactionSummaryByAccount,
  GetPersonAccountsAggregationTypeEnum,
  transactionInternalApi,
  transactionsSummaryApi,
  transactionsSummaryV2Api,
} from '../../apis';
import store from '../../store/store';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import {
  InternalApiNameCardinalitiesByAccountResponse,
  InternalApiTransactionDirection,
  type InternalApiTransactor,
} from '../../api-internal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { saveTransactionSummaryAllTimeEvent } from '../../tracking/userEventTracker';
import dayjs from 'dayjs';

export const fetchAccountsAggregation = async (
  personId: string,
  page: number,
  size: number,
  type: GetPersonAccountsAggregationTypeEnum,
  transactionSummaryV2Enabled: boolean,
  from: Date,
  to: Date
): Promise<ApiTransactionSummaryByAccount[] | undefined> => {
  try {
    if (transactionSummaryV2Enabled) {
      return transactionsSummaryV2Api.getPersonTransactionSummaryByAccount({
        personId,
        page,
        size,
        direction:
          type === GetPersonAccountsAggregationTypeEnum.RECIPIENT
            ? InternalApiTransactionDirection.INCOMING
            : InternalApiTransactionDirection.OUTGOING,
        from,
        to,
      });
    } else {
      return transactionsSummaryApi.getPersonAccountsAggregation({
        personId,
        page,
        size,
        type,
        from,
        to,
      });
    }
  } catch (e) {
    store.dispatch(showFeedback({ message: await parseErrorMessage(e) }));
  }
};

export const useQueryTransactionsSummary = (
  personId: string,
  transactionSummaryV2Enabled: boolean,
  from: Date,
  to: Date,
  transactionType?: string
) => {
  return useQuery({
    queryKey: ['transactionsSummary', personId, transactionSummaryV2Enabled, from, to, transactionType],
    queryFn: async () => {
      if (from.getFullYear() < dayjs().year() - 1) {
        await saveTransactionSummaryAllTimeEvent(personId);
      }
      if (transactionSummaryV2Enabled) {
        return transactionsSummaryV2Api.getPersonTransactionsSummary({
          personId,
          from,
          to,
          transactionType,
        });
      } else {
        return transactionsSummaryApi.getPersonTransactionsSummary({
          personId,
          from,
          to,
        });
      }
    },
    retry: false,
  });
};

export const useQueryNameCardinalitiesByAccount = (accounts: string[], transactorType: InternalApiTransactor) => {
  const queryClient = useQueryClient();

  const queryKey = ['nameCardinalitiesByAccount', transactorType];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const previousData = queryClient.getQueryData<InternalApiNameCardinalitiesByAccountResponse>(queryKey) || {
        nameCardinalitiesByAccount: {},
      };

      const newAccounts = accounts.filter((account) => !previousData.nameCardinalitiesByAccount[account]);

      if (!newAccounts.length) {
        return previousData;
      }

      const newData = await transactionInternalApi.getNameCardinalitiesByAccount({
        internalApiNameCardinalitiesByAccountRequest: {
          accounts: newAccounts,
          transactorType,
        },
      });

      return {
        nameCardinalitiesByAccount: {
          ...previousData.nameCardinalitiesByAccount,
          ...newData.nameCardinalitiesByAccount,
        },
      };
    },
    enabled: false,
    retry: false,
  });
};
